import type { ScenarioTimeline_OrganizationFragment } from '@seaber/common/graphql/types';
import type { JSX } from 'react';
import React, { Fragment } from 'react';
import {
  useIsVoyageCargoVisibleState,
  useIsVoyageChartererVisibleState,
} from '../../state/planning/options';
import { VoyageBoxDataContainer } from './Common.styled';
import type { VoyageAssortment } from './types';
import groupBy from 'lodash/groupBy';
import { sumBy } from 'lodash';
import { formatNumber } from '../../lib/format';
import { isDefined } from '@seaber/common/lib/generics';
import { DeckCargoIndicator, IncomingContractIndicator } from './Indicators';
import { Text } from '../ui';

export function ShipmentAssortmentsField({
  assortments,
  showInquiries,
}: {
  assortments: Array<VoyageAssortment>;
  showInquiries?: boolean;
}): JSX.Element | null {
  const [isVisible] = useIsVoyageCargoVisibleState();
  if (!isVisible) return null;

  const groups = Object.values(groupBy(assortments, (a) => a.assortmentName));
  const lastIndex = groups.length - 1;

  return (
    <VoyageBoxDataContainer>
      {groups.map((group, i) => {
        const { assortmentName: name, amount, amountUnit } = group[0];
        const length = group.length;
        const totalCargoAmount =
          length > 1 ? sumBy(group, (item) => item.amount ?? 0) : null;
        const label =
          length > 1
            ? `${length} * ${name} (${formatNumber(
                totalCargoAmount,
              )} ${amountUnit})`
            : `${name} (${formatNumber(amount)} ${amountUnit})`;

        const incomingContractTypes = showInquiries
          ? Array.from(
              new Set(
                group
                  .map((assortment) => assortment.incomingContractType)
                  .filter(isDefined),
              ),
            )
          : null;

        const showDeckCargoIndicator = group.some(
          (assortment) => assortment.cargoDeckOption === 'DECK',
        );

        return (
          <Fragment key={i}>
            {incomingContractTypes?.map((incomingContractType) => (
              <IncomingContractIndicator
                key={incomingContractType}
                contractType={incomingContractType}
              />
            ))}
            {showDeckCargoIndicator && <DeckCargoIndicator />}
            <ShipmentField
              content={
                <span>
                  {label}
                  {i < lastIndex ? ', ' : ''}
                </span>
              }
            />
          </Fragment>
        );
      })}
    </VoyageBoxDataContainer>
  );
}

export function ShipmentCharterersField({
  charterers,
}: {
  charterers: ScenarioTimeline_OrganizationFragment[];
}): JSX.Element | null {
  const [isVisible] = useIsVoyageChartererVisibleState();
  if (!isVisible) return null;

  const content = [...new Set(charterers.map((org) => org.name))].join(', ');

  return (
    <VoyageBoxDataContainer>
      <ShipmentField content={content} />
    </VoyageBoxDataContainer>
  );
}

export function ShipmentField({
  content,
}: {
  content: React.ReactNode;
}): JSX.Element {
  return (
    <Text
      color="text"
      isUnselectable
      isSingleLine
      fontSize={2}
      overflow="hidden"
      mr={2}
    >
      {content}
    </Text>
  );
}
